import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Globe, MapPin, ShoppingBag } from 'lucide-react';
import axiosInstance from '../../axiosConfig';
import {
  MarketplaceExternalStoreLink,
  MarketplaceFooter,
  MarketplaceHeader,
  MarketplaceSectionHeading,
  ProductCard,
  formatMerchantLocation,
  getMarketplacePath,
  getMerchantInitials,
  getStorefrontLabel,
  getStorefrontUrl
} from './MarketplaceShared';
import MarketplaceSeo, { marketplaceUrlFor } from './MarketplaceSeo';
import './Marketplace.css';

function MarketplaceMerchantPage() {
  const { slug } = useParams();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await axiosInstance.get(`/marketplace/merchants/${slug}`);
        if (!data?.success) {
          throw new Error(data?.message || 'Failed to load merchant');
        }
        if (alive) setPayload(data);
      } catch (err) {
        if (alive) setError(err?.response?.data?.message || err.message || 'Failed to load merchant');
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="marketplace-page">
        <MarketplaceHeader active="merchants" />
        <div className="marketplace-shell marketplace-shell--centered">
          <section className="marketplace-feedback-card marketplace-loading-card">
            <h3>Loading store</h3>
            <p>Pulling live storefront details and product previews.</p>
          </section>
        </div>
      </div>
    );
  }

  if (error || !payload?.merchant) {
    return (
      <div className="marketplace-page">
        <MarketplaceHeader active="merchants" />
        <div className="marketplace-shell marketplace-shell--centered">
          <section className="marketplace-feedback-card">
            <h3>Store not available</h3>
            <p>{error || 'The selected merchant could not be found.'}</p>
          </section>
        </div>
      </div>
    );
  }

  const { merchant, products, meta } = payload;
  const location = formatMerchantLocation(merchant);
  const productLimit = meta?.productLimit || 5;
  const merchantUrl = marketplaceUrlFor(`/merchant/${merchant.slug}`);
  const merchantSeoTitle = merchant.name;
  const merchantSeoDescription = merchant.description
    ? merchant.description
    : `${merchant.name} — live Revstar storefront. Browse ${products.length} products and shop directly from the merchant.`;
  const merchantJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Store',
      '@id': merchantUrl,
      name: merchant.name,
      url: merchantUrl,
      image: merchant.heroImage || merchant.logo || undefined,
      logo: merchant.logo || undefined,
      description: merchant.description || undefined,
      address: (merchant.location?.city || merchant.location?.country) ? {
        '@type': 'PostalAddress',
        addressLocality: merchant.location?.city || undefined,
        addressRegion: merchant.location?.state || undefined,
        addressCountry: merchant.location?.country || undefined
      } : undefined
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Marketplace', item: marketplaceUrlFor('/') },
        { '@type': 'ListItem', position: 2, name: 'Stores', item: marketplaceUrlFor('/merchants') },
        { '@type': 'ListItem', position: 3, name: merchant.name, item: merchantUrl }
      ]
    }
  ];

  return (
    <div className="marketplace-page">
      <MarketplaceSeo
        title={merchantSeoTitle}
        description={merchantSeoDescription}
        path={`/merchant/${merchant.slug}`}
        image={merchant.heroImage || merchant.logo || undefined}
        type="website"
        jsonLd={merchantJsonLd}
      />
      <MarketplaceHeader active="merchants" />

      <div className="marketplace-shell marketplace-shell--spacious">
        <section className="marketplace-breadcrumbs">
          <Link to={getMarketplacePath('/')}>Marketplace</Link>
          <span>/</span>
          <Link to={getMarketplacePath('/merchants')}>Stores</Link>
          <span>/</span>
          <span>{merchant.name}</span>
        </section>

        <section className="marketplace-store-hero">
          <div className="marketplace-store-hero-media">
            {merchant.heroImage ? (
              <img src={merchant.heroImage} alt={merchant.name} />
            ) : (
              <div className="marketplace-image-fallback marketplace-image-fallback--store" role="img" aria-label="Merchant storefront">
                <svg
                  className="marketplace-image-fallback-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M3 9l1-5h16l1 5" />
                  <path d="M4 9h16v11H4z" />
                  <path d="M9 20v-6h6v6" />
                </svg>
              </div>
            )}
          </div>

          <div className="marketplace-store-hero-body">
            <div className="marketplace-store-hero-brand">
              <div className="marketplace-store-hero-logo">
                {merchant.logo ? <img src={merchant.logo} alt={merchant.name} /> : <span>{getMerchantInitials(merchant.name)}</span>}
              </div>
              <div>
                <span className="marketplace-section-eyebrow">Merchant storefront</span>
                <h1>{merchant.name}</h1>
                <p>{getStorefrontLabel(merchant)}</p>
              </div>
            </div>

            <p className="marketplace-store-hero-description">
              {merchant.description || `${merchant.name} sells through a live Revstar storefront. Browse the preview here, then continue to the store for checkout and fulfilment.`}
            </p>

            <div className="marketplace-store-hero-meta">
              <span><MapPin size={15} /> {location}</span>
              <span><ShoppingBag size={15} /> {products.length} of {productLimit} products shown</span>
              <span><Globe size={15} /> Live storefront</span>
            </div>

            <div className="marketplace-store-hero-actions">
              <MarketplaceExternalStoreLink store={merchant}>Visit live storefront</MarketplaceExternalStoreLink>
              <a className="marketplace-subtle-link" href={`${getStorefrontUrl(merchant)}/shop`} target="_blank" rel="noreferrer">
                Browse full catalog
              </a>
            </div>
          </div>
        </section>

        <section className="marketplace-section">
          <MarketplaceSectionHeading
            eyebrow="Store preview"
            title={`Products from ${merchant.name}`}
            description="Marketplace shows a tighter storefront preview. Continue into the merchant’s shop for the full shopping flow."
          />

          <div className="marketplace-product-grid">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </section>
      </div>
      <MarketplaceFooter />
    </div>
  );
}

export default MarketplaceMerchantPage;
